/** @format */

import React, { createContext, useState, useEffect, useContext } from "react";
import { Session, User } from "@supabase/supabase-js";
import { supabase } from "../supabaseClient";
import { useTranslation } from "react-i18next";
import { specialties } from "../lib/specialties";

interface SessionContextType {
  session: Session | null;
  user: User | null;
  userInfo: any | null;
  loading: boolean;
  activePhone: any;
  setActivePhone: any;
  language: string;
  setLanguage: any;
  refreshTokenIfNeeded: () => void;
  setUserInfo: any;
}

const updateSpecialtyLanguage = (userInfo: any, language: string) => {
  if (userInfo?.specialty_id && specialties[userInfo.specialty_id]) {
    const specialty = specialties[userInfo.specialty_id][language];
    return { ...userInfo, specialty };
  }
  return userInfo;
};

const SessionContext = createContext<SessionContextType | undefined>(undefined);

export const SessionProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { t } = useTranslation();
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [userInfo, setUserInfo] = useState<any | null>(null);
  const [language, setLanguage] = useState<string>("es");
  const [loading, setLoading] = useState(true);
  const [activePhone, setActivePhone] = useState<string | null>(null);

  useEffect(() => {
    if (userInfo) {
      const updatedUserInfo = updateSpecialtyLanguage(userInfo, language);
      setUserInfo(updatedUserInfo);
    }
  }, [language]);

  useEffect(() => {
    const initializeSession = async () => {
      const { data } = await supabase.auth.getSession();
      const currentSession = data?.session || null;

      setSession(currentSession);
      setUser(currentSession?.user || null);

      if (currentSession?.user) {
        await Promise.all([
          fetchUserInfo(currentSession),
        ]);
      }

      setLoading(false);
    };

    initializeSession();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (_event, newSession) => {
      setSession(newSession);
      setUser(newSession?.user || null);

      if (newSession?.user) {
        await refreshTokenIfNeeded();
        fetchUserInfo(newSession);
      } else {
        setUserInfo(null);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const refreshTokenIfNeeded = async () => {
    if (!session) return;

    const tokenExpiry = session.expires_at ? session.expires_at * 1000 : null;
    const now = Date.now();

    if (tokenExpiry && tokenExpiry - now < 60000) {
      try {
        const { data, error } = await supabase.auth.refreshSession();

        if (error) {
          console.error("Error refreshing token:", error.message);
          setSession(null);
          setUser(null);
          setUserInfo(null);
        } else {
          setSession(data.session);
          setUser(data.session?.user || null);
        }
      } catch (error) {
        console.error("Error during token refresh:", error);
      }
    }
  };

  const fetchUserInfo = async (session: Session) => {
    await refreshTokenIfNeeded();

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/user/${session.user.id}`,
        {
          method: "GET",
          headers: {
            Authorization: `${session.token_type} ${session.access_token}`,
          },
        }
      );


      if (!response.ok) {
        console.error("Error fetching user info:", response.statusText);
        return;
      }

      const data = await response.json();
      if (data.veterinarian.language) {
        setLanguage(data.veterinarian.language);
      }

      updateFaviconAndTitle();

      setUserInfo(data);
    } catch (error) {
      console.error("Error fetching user info:", error);
    }
  };

  const updateFaviconAndTitle = () => {
    const faviconLink: any = document.querySelector('link[rel="icon"]');

    // Listas de URLs
    const isVeterinarian = process.env.REACT_APP_ENV === "veterinarian"

    if (isVeterinarian) {
      faviconLink.href = "../faviconVet.svg";
      document.title = t("omnivet");
    }

  };

  useEffect(() => {
    const intervalId = setInterval(() => {
      refreshTokenIfNeeded();
    }, 5 * 60 * 1000);

    return () => clearInterval(intervalId);
  }, [session]);

  return (
    <SessionContext.Provider
      value={{
        session,
        user,
        userInfo,
        setUserInfo,
        language,
        setLanguage,
        loading,
        activePhone,
        setActivePhone,
        refreshTokenIfNeeded,
      }}
    >
      {children}
    </SessionContext.Provider>
  );
};

export const useSession = () => {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error("useSession must be used within a SessionProvider");
  }
  return context;
};
